import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@client/components/ui/badge';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useToast } from '@client/hooks/use-toast';
import { ApiClient } from '@client/lib/api-client';
import { queryKeys } from '@client/lib/query-keys';
import { useCrudMutations } from '@client/hooks/useCrudMutations';
import { CrudDialog } from '@client/components/shared/CrudDialog';
import { FormField } from '@client/components/shared/FormField';
import { 
  LinkedInSourceLink,
  ListMetadataDisplay,
  ListOverview, 
  ListDetail 
} from '@client/components/lists';
import type { List, InsertList, Profile } from '@shared/types';

// ✅ Deep-Link Helper Import
import { createListDeepLink } from '@client/utils/deep-link-helpers';

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(date: string): string {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return new Date(date).toLocaleDateString('de-DE');
}

function formatDateFull(date: string): string {
  return new Date(date).toLocaleString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getSourceBadge(source: string) {
  const config: Record<string, { text: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
    'linkedin-search': { text: 'Search', variant: 'default' },
    'linkedin-group': { text: 'Group', variant: 'secondary' },
    'linkedin-company': { text: 'Company', variant: 'secondary' },
    'linkedin-event': { text: 'Event', variant: 'secondary' },
    'linkedin-single': { text: 'Profile', variant: 'outline' },
    'manual': { text: 'Manual', variant: 'outline' },
    'import': { text: 'Import', variant: 'outline' },
    'scraper': { text: 'Scraper', variant: 'default' }
  };
  
  const badgeConfig = config[source] || { text: source, variant: 'outline' as const };
  
  return (
    <Badge variant={badgeConfig.variant}>
      {badgeConfig.text}
    </Badge>
  );
}

function validateListForm(formData: InsertList): string | null {
  if (!formData.name.trim()) return 'List name is required';
  if (formData.name.length > 200) return 'List name must be less than 200 characters';
  return null;
}

// ============================================================================
// Safe API Response Parsers
// ============================================================================

// Pagination-Metadata vom Backend
interface ListPaginationMeta {
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
}

interface ListsApiResponse {
  lists: List[];
  pagination: ListPaginationMeta;
}

function parseListsResponse(data: unknown): ListsApiResponse {
  console.log('[Lists] Parsing API response:', data);

  try {
    if (data && typeof data === 'object' && 'lists' in data && 'pagination' in data) {
      return data as ListsApiResponse;
    }

    // Fallback für Array-Response (Abwärtskompatibilität)
    const lists = Array.isArray(data) ? data as List[] : [];
    return {
      lists,
      pagination: {
        total: lists.length,
        totalPages: 1,
        currentPage: 1,
        pageSize: lists.length,
        hasNextPage: false,
        hasPreviousPage: false,
        startIndex: lists.length > 0 ? 1 : 0,
        endIndex: lists.length,
      }
    };
  } catch (error) {
    console.error('[Lists] Error parsing response:', error);
    return {
      lists: [],
      pagination: {
        total: 0, totalPages: 0, currentPage: 1, pageSize: 20,
        hasNextPage: false, hasPreviousPage: false, startIndex: 0, endIndex: 0,
      }
    };
  }
}

function parseProfilesResponse(data: unknown): Profile[] {
  try {
    if (Array.isArray(data)) return data as Profile[];
    
    if (data && typeof data === 'object') {
      if ('profiles' in data && Array.isArray(data.profiles)) {
        return data.profiles as Profile[];
      }
      if ('data' in data && Array.isArray(data.data)) {
        return data.data as Profile[];
      }
    }
    
    return [];
  } catch (error) {
    console.error('[Lists] Error parsing profiles:', error);
    return [];
  }
}

// ============================================================================
// Main Lists Component
// ============================================================================

export default function Lists() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ============================================================================
  // State
  // ============================================================================

  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editingList, setEditingList] = useState<List | null>(null);
  const [formData, setFormData] = useState<InsertList>({
    name: '',
    description: '',
    source: 'manual',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [listPage, setListPage] = useState(1);
  const [listLimit, setListLimit] = useState(20);

  // ============================================================================
  // Component Mount Effect - Reset State on Navigation
  // ============================================================================

  useEffect(() => {
    console.log('[Lists] Component mounted/updated, id:', id);
    setDialogMode(null);
    setEditingList(null);
    setFormError(null);
  }, [id]);

  // ============================================================================
  // Queries
  // ============================================================================

  // Fetch paginated lists
  const {
    data: listsResponse,
    isLoading,
    error,
    isError
  } = useQuery({
    queryKey: [...queryKeys.lists.all(), { page: listPage, limit: listLimit }],
    queryFn: async () => {
      try {
        const data = await ApiClient.get<unknown>(`/lists?page=${listPage}&limit=${listLimit}`);
        return parseListsResponse(data);
      } catch (error) {
        console.error('[Lists] Error fetching lists:', error);
        throw error;
      }
    },
    staleTime: 30 * 1000,
    retry: 1,
    retryDelay: 1000,
  });

  const lists = listsResponse?.lists ?? [];
  const listPagination = listsResponse?.pagination ?? null;

  // Fetch single list details
  const { 
    data: currentList, 
    isLoading: isLoadingDetail,
    isError: isErrorDetail,
    error: errorDetail
  } = useQuery({
    queryKey: queryKeys.lists.detail(Number(id)),
    queryFn: async () => {
      try {
        const data = await ApiClient.get(`/lists/${id}`);
        console.log('[Lists] Fetched list detail:', data);
        return data as List;
      } catch (error) {
        console.error('[Lists] Error fetching list detail:', error);
        throw error;
      }
    },
    enabled: !!id,
    retry: 1,
  });

  // Fetch profiles for current list
  const { 
    data: profiles = [], 
    isLoading: isLoadingProfiles,
    isError: isErrorProfiles
  } = useQuery({
    queryKey: ['/profiles', { listId: id }],
    queryFn: async () => {
      try {
        const data = await ApiClient.get(`/profiles?listId=${id}`);
        return parseProfilesResponse(data);
      } catch (error) {
        console.error('[Lists] Error fetching profiles:', error);
        throw error;
      }
    },
    enabled: !!id,
    retry: 1,
  });

  // ============================================================================
  // Mutations
  // ============================================================================

  const { createMutation, updateMutation, deleteMutation } = useCrudMutations<List>({
    endpoint: '/lists',
    queryKey: queryKeys.lists.all() as any,
    entityName: 'List',
  });

  // ============================================================================
  // Dialog Handlers
  // ============================================================================

  const openCreateDialog = () => {
    console.log('[Lists] Opening create dialog');
    setFormData({ 
      name: '', 
      description: '', 
      source: 'manual' 
    });
    setFormError(null);
    setEditingList(null);
    setDialogMode('create');
  };

  const openEditDialog = (list: List) => {
    console.log('[Lists] Opening edit dialog for list:', list.id);
    setEditingList(list);
    setFormData({
      name: list.name,
      description: list.description || '',
      source: list.source,
    });
    setFormError(null);
    setDialogMode('edit');
  };

  const closeDialog = () => {
    console.log('[Lists] Closing dialog');
    setDialogMode(null);
    setEditingList(null);
    setFormError(null);
  };

  const handleSave = () => {
    const error = validateListForm(formData);
    if (error) {
      setFormError(error);
      return;
    }

    const saveData: Partial<InsertList> = {
      name: formData.name.trim(),
      description: formData.description?.trim() || undefined,
      source: formData.source,
    };

    if (dialogMode === 'edit' && editingList) {
      updateMutation.mutate({ id: editingList.id, data: saveData }, { 
        onSuccess: () => {
          closeDialog();
          if (id) {
            queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(Number(id)) });
          }
          toast({
            title: 'List Updated',
            description: `"${saveData.name}" has been updated successfully.`
          });
        },
        onError: (error) => {
          toast({
            title: 'Update Failed',
            description: error instanceof Error ? error.message : 'Failed to update list',
            variant: 'destructive'
          });
        }
      });
    } else {
      createMutation.mutate(saveData, { 
        onSuccess: () => {
          closeDialog();
          toast({
            title: 'List Created',
            description: `"${saveData.name}" has been created successfully.`
          });
        },
        onError: (error) => {
          toast({
            title: 'Creation Failed',
            description: error instanceof Error ? error.message : 'Failed to create list',
            variant: 'destructive'
          });
        }
      });
    }
  };

  // ============================================================================
  // Action Handlers
  // ============================================================================

  const handleDelete = (list: List) => {
    if (!confirm(`Are you sure you want to delete "${list.name}"? This will remove ${list.profileCount || 0} profile(s) and cannot be undone.`)) {
      return;
    }

    deleteMutation.mutate(list.id, {
      onSuccess: () => {
        if (id) navigate('/lists');
        toast({
          title: 'List Deleted',
          description: `"${list.name}" has been deleted successfully.`
        });
      },
      onError: (error) => {
        toast({
          title: 'Deletion Failed',
          description: error instanceof Error ? error.message : 'Failed to delete list',
          variant: 'destructive'
        });
      }
    });
  };

  const handleDeleteDetail = () => {
    if (!currentList) return;
    handleDelete(currentList);
  };

  const handleExport = () => {
    toast({ 
      title: 'Export Started', 
      description: 'Profile export is being prepared...' 
    });
    // TODO: Implement export functionality
  };

  // Pagination Handlers
  const handleListPageChange = (page: number) => {
    setListPage(page);
  };

  const handleListLimitChange = (limit: number) => {
    setListPage(1); // Zurück auf Seite 1 bei Limit-Änderung
    setListLimit(limit);
  };

  // ✅ UPDATED: Deep-Link Handler for "View Profiles" Button
  const handleViewProfiles = (list: List) => {
    const deepLink = createListDeepLink(list.id);
    console.log('[Lists] Navigating to Profile Manager with deep-link:', deepLink);
    navigate(deepLink);
  };

  // ============================================================================
  // Error State Rendering
  // ============================================================================

  // Error state for overview
  if (isError && !id) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load lists. {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Error state for detail view
  if (id && isErrorDetail) {
    return (
      <div className="p-6 space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load list details. {errorDetail instanceof Error ? errorDetail.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
        <button 
          onClick={() => navigate('/lists')}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
        >
          Back to Lists
        </button>
      </div>
    );
  }

  // ============================================================================
  // Loading State for Detail View
  // ============================================================================

  if (id && isLoadingDetail) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">Loading list details...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: Detail View
  // ============================================================================

  if (id && currentList) {
    return (
      <>
        <ListDetail
          list={currentList}
          profiles={profiles}
          isLoadingProfiles={isLoadingProfiles}
          onBack={() => navigate('/lists')}
          onEdit={() => openEditDialog(currentList)}
          onDelete={handleDeleteDetail}
          onExport={handleExport}
          onViewAllProfiles={() => handleViewProfiles(currentList)}
          formatDateFull={formatDateFull}
          getSourceBadge={getSourceBadge}
        />

        {/* Edit Dialog for Detail View */}
        <CrudDialog
          open={dialogMode !== null}
          onOpenChange={(open) => !open && closeDialog()}
          mode={dialogMode || 'edit'}
          title="Edit List"
          description={`Change the properties of "${currentList.name}".`}
          onSave={handleSave}
          onCancel={closeDialog}
          isSaving={updateMutation.isPending}
        >
          <div className="space-y-4">
            <FormField
              id="name"
              label="List Name"
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: String(value) })}
              placeholder="Enter list name"
              required
            />
            
            <FormField
              id="description"
              label="Description"
              type="textarea"
              value={formData.description || ''}
              onChange={(value) => setFormData({ ...formData, description: String(value) })}
              placeholder="Optional description"
              rows={3}
            />

            {/* Show current metadata (read-only in edit mode) */}
            {currentList.metadata && Object.keys(currentList.metadata).length > 0 && (
              <div className="border rounded-md p-3 bg-muted">
                <p className="text-sm font-medium mb-2">Current Metadata</p>
                <ListMetadataDisplay list={currentList} variant="compact" />
              </div>
            )}

            {/* Show sourceUrl if exists (read-only) */}
            {currentList.sourceUrl && (
              <div className="border rounded-md p-3 bg-muted">
                <p className="text-sm font-medium mb-2">LinkedIn Source</p>
                <LinkedInSourceLink 
                  list={currentList} 
                  variant="link"
                  showLabel={true}
                />
              </div>
            )}

            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
          </div>
        </CrudDialog>
      </>
    );
  }

  // ============================================================================
  // Render: Overview
  // ============================================================================

  return (
    <>
      <ListOverview
        lists={lists}
        isLoading={isLoading}
        error={error}
        pagination={listPagination}
        onPageChange={handleListPageChange}
        onLimitChange={handleListLimitChange}
        onCreateList={openCreateDialog}
        onViewList={(list) => navigate(`/lists/${list.id}`)}
        onEditList={openEditDialog}
        onDeleteList={handleDelete}
        onViewProfiles={handleViewProfiles}
        formatDate={formatDate}
        getSourceBadge={getSourceBadge}
      />

      {/* Create/Edit Dialog for Overview */}
      <CrudDialog
        open={dialogMode !== null}
        onOpenChange={(open) => !open && closeDialog()}
        mode={dialogMode || 'create'}
        title={dialogMode === 'edit' ? 'Edit List' : 'Create New List'}
        description={
          dialogMode === 'edit'
            ? `Change the properties of "${editingList?.name}".`
            : 'Create a new manual profile list.'
        }
        onSave={handleSave}
        onCancel={closeDialog}
        isSaving={createMutation.isPending || updateMutation.isPending}
      >
        <div className="space-y-4">
          <FormField
            id="name"
            label="List Name"
            value={formData.name}
            onChange={(value) => setFormData({ ...formData, name: String(value) })}
            placeholder="e.g., Hot Leads Q1 2024"
            required
          />

          <FormField
            id="description"
            label="Description"
            type="textarea"
            value={formData.description || ''}
            onChange={(value) => setFormData({ ...formData, description: String(value) })}
            placeholder="Optional description for this list"
            rows={3}
          />

          {/* Info Box for Manual Lists */}
          {dialogMode === 'create' && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Manual lists are created without LinkedIn scraping. You can add profiles to this list later.
              </AlertDescription>
            </Alert>
          )}

          {/* Show metadata in edit mode if exists */}
          {dialogMode === 'edit' && editingList?.metadata && Object.keys(editingList.metadata).length > 0 && (
            <div className="border rounded-md p-3 bg-muted">
              <p className="text-sm font-medium mb-2">List Metadata</p>
              <ListMetadataDisplay list={editingList} variant="compact" />
            </div>
          )}

          {formError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
        </div>
      </CrudDialog>
    </>
  );
}